import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { normalizeFilter } from '@arthelper/shared/image-library';

import { useImages } from '~/context/image';
import { LIBRARY_IMAGES_QUERY } from '~/graphql/queries';
import { chunk } from '~/utils/library-pages';
import { ImageItem } from '~/types';

function rowsOf(data: any): ImageItem[] {
  return (data?.myImages?.edges ?? []).map((edge: { node: ImageItem }) => edge.node);
}

function isId(id: unknown): id is string | number {
  return (typeof id === 'string' && id !== '') || typeof id === 'number';
}

/**
 * The caller's own images by id. `useImages()` only holds the loaded pages, so a row it
 * lacks is read with `myImages(filter: { ids })` (chunks of at most 100, cache-first and
 * watched, so edits elsewhere show up). Owner-scoped on purpose: an id that is not the
 * caller's resolves to null, the same answer the whole-library list used to give.
 */
export function useLibraryImages(ids: readonly unknown[]) {
  const { allImages } = useImages();
  const client = useApolloClient();
  const [fetched, setFetched] = useState<Record<string, ImageItem>>({});

  const loaded = useMemo(() => new Map(allImages.map((row) => [String(row.id), row])), [allImages]);
  const wantedKey = [...new Set(ids.filter(isId).map(String))].join(',');
  const missingKey = useMemo(
    () => (wantedKey ? wantedKey.split(',').filter((id) => !loaded.has(id)) : []).join(','),
    [wantedKey, loaded]
  );

  useEffect(() => {
    if (!missingKey) {
      return undefined;
    }

    const subscriptions = chunk(missingKey.split(',')).map((chunkIds) =>
      client
        .watchQuery({
          query: LIBRARY_IMAGES_QUERY,
          variables: { first: chunkIds.length, filter: normalizeFilter({ ids: chunkIds }) },
          fetchPolicy: 'cache-first',
        })
        .subscribe({
          next: ({ data }) => {
            const rows = rowsOf(data);
            if (rows.length === 0) {
              return;
            }

            setFetched((prev) => rows.reduce((memo, row) => ({ ...memo, [String(row.id)]: row }), prev));
          },
          error: (error) => console.error('[useLibraryImages] lookup failed:', error),
        })
    );

    return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
  }, [client, missingKey]);

  const find = useCallback(
    (id: unknown): ImageItem | null => {
      if (!isId(id)) {
        return null;
      }

      return loaded.get(String(id)) ?? fetched[String(id)] ?? null;
    },
    [loaded, fetched]
  );

  return { find };
}

/** One of the caller's images by id; `null` while it loads or when it is not theirs. */
export function useLibraryImage(id: unknown): ImageItem | null {
  const ids = useMemo(() => (isId(id) ? [id] : []), [id]);
  const { find } = useLibraryImages(ids);

  return find(id);
}
